import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, tap, map, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Article,
  ArticleSummary,
  CreateArticleDto,
  ProposeArticleChangeDto,
  ArticleBacklink,
  ReviewArticleVersionDto,
  ArticleVersion,
  ArticleHistoryEntry
} from '../../types/article.interface';
import { ResolvedArticle } from '../utils/wikilink.utils';
import { TranslationService } from './translation.service';

export interface ArticleFilters {
  page?: number;
  limit?: number;
  visibility?: 'public' | 'personal';
  authorId?: string;
  search?: string;
  tag?: string;
}

export interface PaginatedArticlesResponse {
  data: Article[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ArticleService {
  private readonly http = inject(HttpClient);
  private readonly t = inject(TranslationService);
  private readonly apiUrl = `${environment.apiUrl}/articles`;

  // Local cache of known slugs for wikilink resolution
  private slugCache = signal<Map<string, ResolvedArticle>>(new Map());

  /**
   * Create a new article
   */
  create(dto: CreateArticleDto): Observable<Article> {
    return this.http.post<Article>(this.apiUrl, dto).pipe(
      tap(article => {
        // Update slug cache
        this.slugCache.update(cache => {
          const newCache = new Map(cache);
          newCache.set(article.slug, { slug: article.slug, title: article.title, exists: true });
          return newCache;
        });
      })
    );
  }

  /**
   * Get all articles with optional filtering
   */
  findAll(filters?: ArticleFilters): Observable<PaginatedArticlesResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.visibility) params = params.set('visibility', filters.visibility);
      if (filters.authorId) params = params.set('authorId', filters.authorId);
      if (filters.search) params = params.set('search', filters.search);
      if (filters.tag) params = params.set('tag', filters.tag);
    }

    return this.http.get<PaginatedArticlesResponse>(this.apiUrl, { params });
  }

  /**
   * Get article by slug
   */
  getBySlug(slug: string): Observable<Article> {
    // Check if this is a static article first
    const staticArticle = this.getStaticArticle(slug);
    if (staticArticle) {
      return staticArticle;
    }
    
    // Otherwise fetch from API
    return this.http.get<Article>(`${this.apiUrl}/slug/${slug}`);
  }

  /**
   * Load static articles from assets folder
   */
  private getStaticArticle(slug: string): Observable<Article> | null {
    const staticArticles: Record<string, { title: string; file: string }> = {
      'terminos-y-condiciones': {
        title: this.t.instant('articleService.termsOfService'),
        file: 'assets/data/tyc.md'
      },
      'preguntas-frecuentes': {
        title: this.t.instant('articleService.faq'),
        file: 'assets/data/faq.md'
      },
      'contacto': {
        title: this.t.instant('articleService.contact'),
        file: 'assets/data/contacto.md'
      },
      'donar': {
        title: this.t.instant('articleService.donation'),
        file: 'assets/data/donacion.md'
      }
    };

    const config = staticArticles[slug];
    if (!config) {
      return null;
    }

    return this.http.get(config.file, { responseType: 'text' }).pipe(
      map(content => ({
        id: `static-${slug}`,
        slug: slug,
        title: config.title,
        content: content,
        visibility: 'public' as const,
        status: 'published' as const,
        authorId: 'system',
        authorName: 'Remateriar',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })),
      catchError(() => {
        // If static file doesn't exist, return null to fall through to API
        throw new Error('Static article not found');
      })
    );
  }

  /**
   * Get article by ID
   */
  getById(id: string): Observable<Article> {
    return this.http.get<Article>(`${this.apiUrl}/${id}`);
  }

  /**
   * Delete an article
   */
  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Batch lookup slugs for wikilink resolution.
   * Returns a Map<slug, ResolvedArticle> with existence and title info.
   */
  lookupSlugs(slugs: string[]): Observable<Map<string, ResolvedArticle>> {
    if (slugs.length === 0) {
      return of(new Map());
    }

    // Check cache first
    const cached = this.slugCache();
    const uncached = slugs.filter(s => !cached.has(s));

    if (uncached.length === 0) {
      // All slugs are cached
      const result = new Map<string, ResolvedArticle>();
      slugs.forEach(s => {
        const entry = cached.get(s);
        if (entry) result.set(s, entry);
      });
      return of(result);
    }

    // Fetch uncached slugs from API
    return this.http.post<ArticleSummary[]>(`${this.apiUrl}/check-slugs`, { slugs: uncached }).pipe(
      map(summaries => {
        const result = new Map<string, ResolvedArticle>();

        // Add cached entries
        slugs.forEach(s => {
          const entry = cached.get(s);
          if (entry) result.set(s, entry);
        });

        // Add fresh entries from API
        summaries.forEach(s => {
          const resolved: ResolvedArticle = {
            slug: s.slug,
            title: s.title,
            exists: s.exists
          };
          result.set(s.slug, resolved);
        });

        // Mark any slug not returned by API as non-existing
        uncached.forEach(s => {
          if (!result.has(s)) {
            result.set(s, { slug: s, title: s, exists: false });
          }
        });

        // Update cache
        this.slugCache.update(cache => {
          const newCache = new Map(cache);
          result.forEach((v, k) => newCache.set(k, v));
          return newCache;
        });

        return result;
      }),
      catchError(() => {
        // On error, return all slugs as non-existing (graceful degradation)
        const result = new Map<string, ResolvedArticle>();
        slugs.forEach(s => {
          const entry = cached.get(s) || { slug: s, title: s, exists: false };
          result.set(s, entry);
        });
        return of(result);
      })
    );
  }

  /**
   * Get cached slug map (synchronous)
   */
  getCachedSlugMap(): Map<string, ResolvedArticle> {
    return this.slugCache();
  }

  /**
   * Manually add a slug to the cache (e.g., after creating an article)
   */
  addToCache(slug: string, title: string, exists: boolean): void {
    this.slugCache.update(cache => {
      const newCache = new Map(cache);
      newCache.set(slug, { slug, title, exists });
      return newCache;
    });
  }

  /**
   * Get backlinks — articles that link to a given slug
   */
  getBacklinks(slug: string): Observable<ArticleBacklink[]> {
    return this.http.get<ArticleBacklink[]>(`${this.apiUrl}/${slug}/backlinks`);
  }

  /**
   * Propose changes to a public article
   */
  proposeChange(dto: ProposeArticleChangeDto): Observable<any> {
    return this.http.post(`${this.apiUrl}/${dto.articleId}/propose-changes`, {
      content: dto.content,
      comment: dto.changeDescription
    });
  }

  /**
   * Check which slugs exist (batch). Uses the /check-slugs endpoint.
   */
  checkSlugs(slugs: string[]): Observable<ArticleSummary[]> {
    return this.http.post<ArticleSummary[]>(`${this.apiUrl}/check-slugs`, { slugs });
  }

  /**
   * Search articles by query string (for autocomplete in wikilink editor).
   * Uses POST /articles/check-slugs as per doc section 8.1.
   */
  searchArticles(query: string, limit: number = 8): Observable<ArticleSummary[]> {
    if (!query || query.length < 2) {
      return of([]);
    }
    return this.http.post<ArticleSummary[]>(`${this.apiUrl}/check-slugs`, {
      slugs: [query],
      search: query,
      limit
    }).pipe(
      catchError(() => of([]))
    );
  }

  // --- Entity-linked articles ---

  /**
   * Get the article for a specific entity (e.g., material, center).
   * GET /articles/entity/:type/:entityId
   */
  getByEntity(entityType: string, entityId: string): Observable<Article> {
    return this.http.get<Article>(`${this.apiUrl}/entity/${entityType}/${entityId}`);
  }

  // --- Version history ---

  /**
   * Get all versions with review status, editor info, and diffs.
   * GET /articles/:id/history
   */
  getHistory(articleId: string): Observable<ArticleHistoryEntry[]> {
    return this.http.get<ArticleHistoryEntry[]>(`${this.apiUrl}/${articleId}/history`);
  }

  // --- Community review ---

  /**
   * Submit a community review for a version.
   * POST /articles/versions/:versionId/review
   */
  reviewVersion(versionId: string, dto: ReviewArticleVersionDto): Observable<any> {
    return this.http.post(`${this.apiUrl}/versions/${versionId}/review`, dto);
  }

  // --- Admin endpoints ---

  /**
   * List all pending versions (admin/manager only).
   * GET /articles/admin/pending
   */
  getAdminPending(): Observable<ArticleVersion[]> {
    return this.http.get<ArticleVersion[]>(`${this.apiUrl}/admin/pending`);
  }

  /**
   * Directly approve a version (admin, bypasses community review).
   * POST /articles/versions/:versionId/approve
   */
  approveVersion(versionId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/versions/${versionId}/approve`, {});
  }

  /**
   * Directly reject a version (admin).
   * POST /articles/versions/:versionId/reject
   */
  rejectVersion(versionId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/versions/${versionId}/reject`, {});
  }

  /**
   * Publish an article (requires approved version).
   * PATCH /articles/:id/publish
   */
  publish(articleId: string): Observable<Article> {
    return this.http.patch<Article>(`${this.apiUrl}/${articleId}/publish`, {});
  }

  /**
   * Archive an article.
   * PATCH /articles/:id/archive
   */
  archive(articleId: string): Observable<Article> {
    return this.http.patch<Article>(`${this.apiUrl}/${articleId}/archive`, {});
  }
}
